import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AlertTriangle, CalendarX2, SearchX, WifiOff, type LucideIcon } from 'lucide-react';
import {
  slugFromHost,
  type PublicBookingPage,
  type PublicCheckInPlace,
  type PublicClinicPage,
} from '@rx-manager/shared';
import type { Lang } from '@rx-manager/forms';
import { bookingApi, BookingApiError, checkInApi, type FormPharmacy } from './lib/api';
import {
  I18nProvider,
  isLocale,
  LOCALES,
  useI18n,
  type Locale,
  type MessageKey,
} from './lib/i18n';
import { Button, HEADING, IconTile, Spinner } from './components/ui';
import { LanguageSwitch, LoadingFrame, OfflineNotice, Shell } from './components/Shell';
import { BookingFlow } from './pages/BookingFlow';
import { ManagePage } from './pages/ManagePage';
import { WaitlistEntryPage } from './pages/waitlist/WaitlistEntryPage';
import { OfferPage } from './pages/waitlist/OfferPage';
import { FormPage } from './pages/forms/FormPage';
import { PublicFormPage } from './pages/forms/PublicFormPage';
import { ClinicIntro } from './pages/clinic/Clinic';
import { RefillPage } from './pages/refill/RefillPage';
import { CheckInPage } from './pages/check-in/CheckInPage';
import { QueuePage } from './pages/check-in/QueuePage';
import { LobbyPage } from './pages/check-in/LobbyPage';
import { LinkProblem, useCarriedLocale } from './pages/check-in/shared';

/**
 * One deployment, every pharmacy.
 *
 * The slug in the path picks the tenant, so nothing here is built per pharmacy
 * and a fix ships once for everybody. That is the whole reason this is a
 * separate app rather than something copied into each pharmacy's website: under
 * the old design, fixing a booking bug meant rebuilding the desktop app and
 * asking every pharmacy to republish.
 *
 * `manage` cannot collide with a pharmacy slug because it is a reserved word on
 * the server — see `booking-profile.service.ts`.
 */
/**
 * The booking domain, e.g. `myonerx.ca`. A pharmacy is one label under it.
 */
const BASE_DOMAIN: string =
  (import.meta.env.VITE_BOOKING_BASE_DOMAIN as string | undefined) ?? 'myonerx.ca';

/**
 * The pharmacy this page is for.
 *
 * The hostname wins when it names one — `two-hills.myonerx.ca` — because that is
 * the public address and its path is just `/`. Otherwise the slug comes from the
 * path, which is what `book.myonerx.ca/two-hills` uses and the only form that
 * works on localhost, where wildcard subdomains would need a hosts-file entry.
 */
function useSlug(): string {
  const { slug: fromPath } = useParams();
  const fromHost =
    typeof window === 'undefined'
      ? null
      : slugFromHost(window.location.hostname, BASE_DOMAIN);
  return fromHost ?? fromPath ?? '';
}

/**
 * The service a deep link arrived with, if any.
 *
 * On the subdomain form the hostname already names the pharmacy, so the single
 * path segment is the service rather than the slug — `/:slug` and
 * `/:slug/:serviceId` are the same URL shape read two different ways depending
 * on where the tenant came from.
 */
function useDeepLinkedService(): string | undefined {
  const { slug: first, serviceId: second } = useParams();
  const hostSlug =
    typeof window === 'undefined'
      ? null
      : slugFromHost(window.location.hostname, BASE_DOMAIN);
  return (hostSlug ? (second ?? first) : second) || undefined;
}

/** Track BCW — the condition a `/c/<code>` link arrived with, if any (BCW-D5). */
function useDeepLinkedCondition(): string | undefined {
  const { code } = useParams();
  return code || undefined;
}

/** Track CGS — the clinic a `/clinic/<code>` link opens, if any (CGS-D2). */
function useDeepLinkedClinic(): string | undefined {
  const { clinicCode } = useParams();
  return clinicCode || undefined;
}

/** A page is a clinic's own when the server sent its clinic (CGS-D3). */
function clinicOf(page: PublicBookingPage | PublicClinicPage): PublicClinicPage['clinic'] | undefined {
  return 'clinic' in page ? page.clinic : undefined;
}

/**
 * Track CGS (CGS-D2) — where a listed clinic's own page is, in the form this page was reached
 * by: on the pharmacy's subdomain, on the shared host, or inside the pharmacy's own website.
 */
function clinicPath(slug: string, embedded: boolean): (code: string) => string {
  const onHost =
    typeof window !== 'undefined' && slugFromHost(window.location.hostname, BASE_DOMAIN) !== null;
  return (code) =>
    embedded
      ? `/embed/${encodeURIComponent(slug)}/clinic/${encodeURIComponent(code)}`
      : onHost
        ? `/clinic/${encodeURIComponent(code)}`
        : `/${encodeURIComponent(slug)}/clinic/${encodeURIComponent(code)}`;
}

/** The page, or a clinic's own page when the link names one. */
function loadPage(slug: string, clinicCode: string | undefined): Promise<PublicBookingPage | PublicClinicPage> {
  return clinicCode ? bookingApi.clinicPage(slug, clinicCode) : bookingApi.page(slug);
}

/**
 * A language the page was explicitly asked for — `?lang=fr` from the pharmacy's
 * own French page or its embedded picker (A5 FE-P-09). It beats the browser,
 * because the patient is already reading that language.
 */
function useRequestedLocale(): Locale | undefined {
  const [params] = useSearchParams();
  const lang = params.get('lang');
  return isLocale(lang) ? lang : undefined;
}

/** The pharmacy's default, used when the browser asks for neither English nor French (Q11). */
function defaultOf(page: PublicBookingPage): Locale | undefined {
  return isLocale(page.profile.defaultLocale) ? page.profile.defaultLocale : undefined;
}

/**
 * A 404 for a named pharmacy means its page is not published (or the name is
 * wrong) — either way it isn't taking online bookings here, which is the
 * useful thing to say.
 */
const forPharmacy = (kind: ProblemKind): ProblemKind => (kind === 'missing' ? 'closed' : kind);

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/manage/:token" element={<ManageShell />} />
        {/*
          Track SFM — a patient form, by its link. "f" is shorter than any
          slug can be (three characters at least) and a static segment outranks
          `/:slug/:serviceId`, so these can never be taken for a pharmacy.
        */}
        {/*
          Track BCW — a place on the waitlist (`/w/`) and an offer (`/o/`), by their links: one
          letter, like `f`, so never a pharmacy's slug.
        */}
        <Route path="/w/:token" element={<WaitlistShell />} />
        <Route path="/o/:token" element={<OfferShell />} />
        {/*
          Track CIQ — checking in at a place (`/i/<code>`: its poster's QR, and the counter's
          kiosk with `?kiosk=1`), the patient's own place in line (`/q/`) and a lobby screen
          (`/l/`). One letter each, like `f`, so never a pharmacy's slug.
        */}
        <Route path="/i/:code" element={<CheckInShell />} />
        <Route path="/q/:token" element={<QueueShell />} />
        <Route path="/l/:token" element={<LobbyShell />} />
        <Route path="/f/p/:token" element={<FormShell poster />} />
        <Route path="/f/:token" element={<FormShell />} />
        {/* The subdomain form: the pharmacy is in the hostname, path is "/". */}
        <Route path="/" element={<PharmacyShell />} />
        {/*
          Track BCW — a condition's own link (BCW-D5): `/c/<code>` on a pharmacy's subdomain,
          `/<slug>/c/<code>` on the shared host. The static "c" outranks `/:slug/:serviceId`.
        */}
        <Route path="/c/:code" element={<PharmacyShell />} />
        <Route path="/:slug/c/:code" element={<PharmacyShell />} />
        {/*
          Track CGS — a clinic's own page (CGS-D2): `/clinic/<code>` on a pharmacy's subdomain,
          `/<slug>/clinic/<code>` on the shared host. "clinic" is a reserved slug, and the static
          segment outranks `/:slug/:serviceId`.
        */}
        <Route path="/clinic/:clinicCode" element={<PharmacyShell />} />
        <Route path="/:slug/clinic/:clinicCode" element={<PharmacyShell />} />
        {/*
          Track ORX — a refill, renewal or transfer request: `/r` on a pharmacy's subdomain,
          `/<slug>/r` on the shared host. "r" is one letter, like `f`, so never a slug, and the
          static segment outranks `/:slug` and `/:slug/:serviceId`.
        */}
        <Route path="/r" element={<RefillShell />} />
        <Route path="/:slug/r" element={<RefillShell />} />
        {/*
          Deep links from the pharmacy's own site.
          Both forms carry a service, because the booking pages the website
          builder generates are per service — a visitor arriving from
          `/book/flu-shot` has already chosen, and asking again is a step that
          measurably loses people.

          The subdomain form has the pharmacy in the hostname, so its single
          path segment IS the service; the path form has both. Without the
          second route every "Choose a time" button on every published booking
          page fell through to the catch-all and said "not found".
        */}
        <Route path="/:slug" element={<PharmacyShell />} />
        <Route path="/:slug/:serviceId" element={<PharmacyShell />} />
        {/*
          The same flow, framed inside the pharmacy's OWN website.

          It is a route rather than a build flag because it is the same code,
          the same API and the same audit trail — only the chrome differs. The
          pharmacy's page already carries their logo, their navigation and
          their footer, so repeating ours inside the frame would be a second
          header on one page.
        */}
        <Route path="/embed/:slug" element={<EmbedShell />} />
        <Route path="/embed/:slug/:serviceId" element={<EmbedShell />} />
        <Route path="/embed/:slug/c/:code" element={<EmbedShell />} />
        <Route path="/embed/:slug/clinic/:clinicCode" element={<EmbedShell />} />
        <Route path="/embed/:slug/r" element={<RefillEmbedShell />} />
        <Route path="*" element={<NotFoundShell />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * The pharmacy's page: fetch first, then render everything inside its own
 * language settings.
 *
 * The profile has to arrive before the shell can render, because the shell's
 * language switcher offers only what this pharmacy publishes.
 */
function PharmacyShell() {
  const slug = useSlug();
  const deepLinkedService = useDeepLinkedService();
  const deepLinkedCondition = useDeepLinkedCondition();
  const clinicCode = useDeepLinkedClinic();
  const requested = useRequestedLocale();
  const [page, setPage] = useState<PublicBookingPage | PublicClinicPage | null>(null);
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    // Reached the apex or a bare `/` with no pharmacy in either place.
    if (!slug) {
      setState('missing');
      return;
    }
    let cancelled = false;
    setState('loading');
    void loadPage(slug, clinicCode)
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setState('ready');
        document.title = `${clinicOf(result)?.name ?? result.profile.displayName} — Book an appointment`;
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(forPharmacy(problemKind(error)));
      });
    return () => {
      cancelled = true;
    };
  }, [slug, clinicCode]);

  if (state === 'loading') {
    // No provider yet, so this one string cannot be translated. It is on screen
    // for a few hundred milliseconds before the real page replaces it.
    return <LoadingFrame label="Loading…" />;
  }

  if (state !== 'ready' || !page) {
    // `ready` with no page cannot happen; treating it as a generic failure keeps
    // the type honest without inventing a fifth state for it.
    const kind: ProblemKind = state === 'ready' ? 'error' : state;
    return (
      <I18nProvider initial={requested}>
        <Shell>
          <Problem kind={kind} />
        </Shell>
      </I18nProvider>
    );
  }

  const available = page.profile.locales.filter(isLocale);
  const clinic = clinicOf(page);
  return (
    <I18nProvider
      available={available.length > 0 ? available : (['en'] as Locale[])}
      initial={requested}
      fallback={defaultOf(page)}
    >
      <Shell
        // Track CGS (CGS-D3) — a clinic's page names the clinic, its address and phone, run by
        // the pharmacy.
        title={clinic ? clinic.name : page.profile.displayName}
        blurb={clinic ? page.profile.displayName : page.profile.blurb}
        logoUrl={page.profile.logoUrl}
        supportEmail={page.profile.supportEmail}
        supportPhone={clinic?.phone ?? page.profile.supportPhone}
        // One branch: where it is, on the band. With several, the branch step says.
        address={clinic ? clinic.address : page.locations.length === 1 ? page.locations[0]!.address : null}
        bookingPage
      >
        {clinic && <ClinicIntro clinic={clinic} />}
        <BookingFlow
          page={page}
          initialServiceId={deepLinkedService}
          initialConditionCode={deepLinkedCondition}
          clinic={clinic}
          clinicHref={clinicPath(slug, false)}
        />
      </Shell>
    </I18nProvider>
  );
}

/**
 * The booking flow as an embedded widget.
 *
 * ## Why an iframe rather than script-rendered into the page
 *
 * The pages the website builder generates keep every indexable word — service
 * descriptions, hours, JSON-LD — native to the pharmacy's own domain, which is
 * the whole SEO argument for generating them. None of that applies to a booking
 * *form*: nobody searches for one, so framing it costs no ranking while buying
 * two things that matter.
 *
 * 1. **The patient's details stay in our origin.** A pharmacy's site may carry
 *    analytics, a chat widget, or whatever a previous agency left behind. Those
 *    scripts can read any DOM and any keystroke on the page they run in. Same-
 *    origin policy means they cannot reach inside this frame — so a date of
 *    birth and a health card number are not typed into a document a third party
 *    can observe.
 * 2. **The stylesheet cannot fight.** Six templates, each with its own reset and
 *    type scale, would all be applied to a form built for none of them.
 *
 * The patient never leaves the pharmacy's website, which is the point.
 *
 * ## Chrome
 *
 * Deliberately bare: no header, no logo, no footer. The page around the frame
 * is already the pharmacy's, and a second masthead inside it reads as a bug.
 * The language switcher survives, because a bilingual pharmacy's patient needs
 * it and the surrounding page cannot drive what is inside the frame.
 */
function EmbedShell() {
  const slug = useSlug();
  const deepLinkedService = useDeepLinkedService();
  const deepLinkedCondition = useDeepLinkedCondition();
  const clinicCode = useDeepLinkedClinic();
  const requested = useRequestedLocale();
  const [page, setPage] = useState<PublicBookingPage | PublicClinicPage | null>(null);
  const [state, setState] = useState<PageState>('loading');

  useEffect(() => {
    if (!slug) {
      setState('missing');
      return;
    }
    let cancelled = false;
    setState('loading');
    void loadPage(slug, clinicCode)
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(forPharmacy(problemKind(error)));
      });
    return () => {
      cancelled = true;
    };
  }, [slug, clinicCode]);

  const contentRef = useEmbedHeight();

  if (state === 'loading') {
    return (
      <div className="p-6">
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (state !== 'ready' || !page) {
    const kind: ProblemKind = state === 'ready' ? 'error' : state;
    return (
      <I18nProvider initial={requested}>
        <div className="p-4">
          <Problem kind={kind} />
        </div>
      </I18nProvider>
    );
  }

  const available = page.profile.locales.filter(isLocale);
  return (
    <I18nProvider
      available={available.length > 0 ? available : (['en'] as Locale[])}
      initial={requested}
      fallback={defaultOf(page)}
    >
      {/* Transparent, so the frame sits on the pharmacy's own background
          instead of punching a white rectangle through their design. The ref is
          what the host page's height sync measures — see useEmbedHeight. */}
      <div ref={contentRef} className="bg-transparent p-1">
        {available.length > 1 && (
          <div className="mb-2 flex justify-end">
            <LanguageSwitch />
          </div>
        )}
        {clinicOf(page) && <ClinicIntro clinic={clinicOf(page)!} />}
        <BookingFlow
          page={page}
          initialServiceId={deepLinkedService}
          initialConditionCode={deepLinkedCondition}
          clinic={clinicOf(page)}
          clinicHref={clinicPath(slug, true)}
        />
      </div>
    </I18nProvider>
  );
}

/** Inside another page's frame: the pharmacy website's booking frame (`picker.ts`). */
function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

/**
 * Tells the host page how tall this frame needs to be.
 *
 * An iframe has no intrinsic height, so without this the booking flow either
 * scrolls inside a short box or leaves a gap below itself — and it changes
 * height at every step, which is exactly when a patient is reading it.
 *
 * ## Measure the CONTENT, not the document
 *
 * `html`, `body` and `#root` are all `height: 100%`, which inside a frame means
 * they report the *frame's* height, not the content's. Measuring them produces
 * a loop that can never grow: the frame is 520px, so the document measures
 * 520px, so the frame stays 520px, and a form that needs 900px scrolls inside a
 * box forever. So the caller attaches this ref to the element that actually
 * wraps the content, and that is what gets observed.
 *
 * `'*'` as the target origin is safe because the only thing sent is a number.
 * The listening side is the part that must be careful, and it checks the sender
 * — see `picker.ts`.
 */
function useEmbedHeight(): (node: HTMLDivElement | null) => void {
  const observer = useRef<ResizeObserver | null>(null);

  // A CALLBACK ref, not a `useRef` + `useEffect`. The wrapper only exists once
  // the pharmacy's profile has loaded, so an effect with an empty dependency
  // list runs while the shell is still showing a spinner, finds no element, and
  // never runs again — the frame then keeps its placeholder height forever.
  // React calls this the moment the node attaches, and again with null when it
  // detaches, which is exactly the lifetime the observer needs.
  return useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node || !isEmbedded()) {
      return;
    }

    const post = (): void => {
      // Plus the wrapper's own padding, so the last button is not flush against
      // the bottom edge of the frame.
      const height = Math.ceil(node.getBoundingClientRect().height) + 8;
      window.parent.postMessage({ type: 'rx-booking:height', height }, '*');
    };

    post();
    observer.current = new ResizeObserver(post);
    observer.current.observe(node);
  }, []);
}

/**
 * A page that must stay private while it is open: not indexed, and its
 * address (which holds a token) never sent on as a referrer. The host's
 * `_headers` says the same for /f/ and /manage/; this covers any host.
 */
function usePrivatePage(): void {
  useEffect(() => {
    const set = (name: string, content: string): (() => void) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      const created = !meta;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      const before = meta.content;
      meta.content = content;
      return () => {
        if (created) meta.remove();
        else meta.content = before;
      };
    };
    const undo = [set('robots', 'noindex, nofollow'), set('referrer', 'no-referrer')];
    return () => undo.forEach((fn) => fn());
  }, []);
}

/**
 * A patient form (Track SFM): the pharmacy's name across the top, and the
 * languages the form itself is written in. The tab title names the pharmacy,
 * never the form: a form's title can name a condition, and a phone's tab list
 * and history are not private.
 */
function FormShell({ poster = false }: { poster?: boolean }) {
  const { token = '' } = useParams();
  const requested = useRequestedLocale();
  const [pharmacy, setPharmacy] = useState<FormPharmacy | null>(null);
  const [languages, setLanguages] = useState<Locale[]>([...LOCALES]);
  usePrivatePage();

  useEffect(() => {
    document.title = pharmacy ? pharmacy.displayName : 'Form';
  }, [pharmacy]);

  const onLanguages = useCallback((offered: Lang[]) => {
    const usable = offered.filter(isLocale);
    const next: Locale[] = usable.length > 0 ? usable : ['en'];
    // The same languages keep the same list: a new one re-renders everything under the provider.
    setLanguages((current) =>
      current.length === next.length && current.every((locale, i) => locale === next[i]) ? current : next,
    );
  }, []);
  const contentRef = useEmbedHeight();

  const page = poster ? (
    <PublicFormPage key={token} publicToken={token} onPharmacy={setPharmacy} onLanguages={onLanguages} />
  ) : (
    <FormPage key={token} token={token} onPharmacy={setPharmacy} onLanguages={onLanguages} />
  );
  return (
    <I18nProvider available={languages} initial={requested}>
      {isEmbedded() ? (
        // "Fill in your form" on the done screen of a booking made in the pharmacy website's frame
        // (picker.ts) opens the form in that same frame. As there, the website is the pharmacy's
        // own page, so no second header, and the frame follows the form's height. Not Shell:
        // its `min-h-full` would measure the frame itself, so the frame could only ever grow.
        <div ref={contentRef} className="bg-transparent p-1">
          {languages.length > 1 && (
            <div className="mb-2 flex justify-end">
              <LanguageSwitch />
            </div>
          )}
          <OfflineNotice />
          {page}
        </div>
      ) : (
        <Shell
          title={pharmacy?.displayName}
          logoUrl={pharmacy?.logoUrl}
          supportPhone={pharmacy?.supportPhone}
        >
          {page}
        </Shell>
      )}
    </I18nProvider>
  );
}

/**
 * Track ORX — the refill, renewal and transfer page. It learns the pharmacy's name and number from
 * its own route (`GET public/refill/:slug`), so the band is named once that answers, and every
 * language is offered, as on the manage page. Private like a form: not indexed, no referrer.
 */
function RefillShell() {
  const slug = useSlug();
  const requested = useRequestedLocale();
  usePrivatePage();
  return (
    <I18nProvider initial={requested}>
      <Shell>
        <RefillPage slug={slug} />
      </Shell>
    </I18nProvider>
  );
}

/** The same page inside the pharmacy's own website's frame: no header, the frame follows its height. */
function RefillEmbedShell() {
  const slug = useSlug();
  const requested = useRequestedLocale();
  const contentRef = useEmbedHeight();
  usePrivatePage();
  return (
    <I18nProvider initial={requested}>
      <div ref={contentRef} className="bg-transparent p-1">
        <div className="mb-2 flex justify-end">
          <LanguageSwitch />
        </div>
        <OfflineNotice />
        <RefillPage slug={slug} />
      </div>
    </I18nProvider>
  );
}

/**
 * The manage page has no pharmacy context until its token resolves, so it
 * offers every language rather than guessing at one.
 */
function ManageShell() {
  usePrivatePage();
  return (
    <I18nProvider>
      <Shell>
        <ManagePage />
      </Shell>
    </I18nProvider>
  );
}

/** Track BCW — a place on the waitlist: private, like the manage page. */
function WaitlistShell() {
  usePrivatePage();
  return (
    <I18nProvider>
      <Shell>
        <WaitlistEntryPage />
      </Shell>
    </I18nProvider>
  );
}

/** Track BCW — a time offered from the waitlist: private, like the manage page. */
function OfferShell() {
  usePrivatePage();
  return (
    <I18nProvider>
      <Shell>
        <OfferPage />
      </Shell>
    </I18nProvider>
  );
}

/**
 * Track CIQ (CIQ-D2, D7) — a place's check-in page, by its poster's code. Private like a form: the
 * code names a place, never a person, but what is typed here is not for an index or a referrer.
 *
 * The place comes first, as a pharmacy's page does: its brand names the band and its languages
 * are the ones offered. The kiosk (`?kiosk=1`) wraps it all in `kiosk`, a size larger throughout,
 * and leaves the band's phone off: nothing on a counter's tablet leads away from the page.
 */
function CheckInShell() {
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const kiosk = params.get('kiosk') === '1';
  const requested = useRequestedLocale();
  const [place, setPlace] = useState<PublicCheckInPlace | null>(null);
  const [state, setState] = useState<PageState>('loading');
  usePrivatePage();

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    void checkInApi
      .place(code)
      .then((result) => {
        if (cancelled) return;
        setPlace(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(problemKind(error));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  /**
   * The place again, without a spinner: walk-ins open and close through the day, and a kiosk is
   * left on all day. A failed look keeps what is on screen; a code made new since ends the page.
   */
  const refresh = useCallback(async (): Promise<PublicCheckInPlace | null> => {
    try {
      const result = await checkInApi.place(code);
      setPlace(result);
      return result;
    } catch (error) {
      if (error instanceof BookingApiError && error.isMissing) setState('missing');
      return null;
    }
  }, [code]);

  const languages = place?.locales.join(',') ?? '';
  const available = useMemo(() => {
    const offered = languages.split(',').filter(isLocale);
    return offered.length > 0 ? offered : (['en'] as Locale[]);
  }, [languages]);

  if (state === 'loading') {
    // No language yet: the place says which it offers.
    return <LoadingFrame label="Loading…" />;
  }

  const frame = (content: ReactNode) => (kiosk ? <div className="kiosk h-full">{content}</div> : <>{content}</>);

  if (state !== 'ready' || !place) {
    const kind: ProblemKind = state === 'ready' ? 'error' : state;
    return frame(
      <I18nProvider initial={requested}>
        <Shell>{kind === 'missing' ? <LinkProblem message="checkIn.missing" /> : <Problem kind={kind} />}</Shell>
      </I18nProvider>,
    );
  }

  return frame(
    <I18nProvider
      available={available}
      initial={requested}
      fallback={isLocale(place.defaultLocale) ? place.defaultLocale : undefined}
    >
      <Shell title={place.brand.name} logoUrl={place.brand.logoUrl} supportPhone={kiosk ? null : place.brand.phone}>
        <CheckInPage code={code} place={place} kiosk={kiosk} onRefresh={refresh} />
      </Shell>
    </I18nProvider>,
  );
}

/**
 * Track CIQ (CIQ-D16) — the patient's own place in line: private, like the manage page. It opens in
 * the language they checked in in, carried in the router's state, unless the address asks for one.
 */
function QueueShell() {
  const requested = useRequestedLocale();
  const carried = useCarriedLocale();
  usePrivatePage();
  return (
    <I18nProvider initial={requested ?? carried}>
      <Shell>
        <QueuePage />
      </Shell>
    </I18nProvider>
  );
}

/**
 * Track CIQ (CIQ-D28) — a lobby screen on a TV: the whole screen, no band. Private all the same:
 * its link is the screen's only key. A TV can't be asked, so `?lang=fr` picks French.
 */
function LobbyShell() {
  const requested = useRequestedLocale();
  usePrivatePage();
  return (
    <I18nProvider initial={requested}>
      <LobbyPage />
    </I18nProvider>
  );
}

function NotFoundShell() {
  return (
    <I18nProvider>
      <Shell>
        <Problem kind="missing" />
      </Shell>
    </I18nProvider>
  );
}

export type PageState = 'loading' | 'ready' | ProblemKind;
export type ProblemKind = 'missing' | 'closed' | 'offline' | 'error';

/**
 * Which of the three a failure actually is.
 *
 * The distinction matters more than it looks. An unreachable API — the server
 * down, or a CORS origin nobody added — used to surface as "Something went
 * wrong" under advice to check the address, which sends a patient hunting for a
 * typo that is not there. "We could not reach the pharmacy" is both true and
 * actionable.
 */
export function problemKind(error: unknown): ProblemKind {
  if (!(error instanceof BookingApiError)) return 'error';
  if (error.isMissing) return 'missing';
  // Status 0: the request never reached a server at all.
  if (error.status === 0) return 'offline';
  return 'error';
}

const PROBLEM_TEXT: Record<ProblemKind, { heading: MessageKey; help?: MessageKey; icon: LucideIcon }> = {
  missing: { heading: 'error.missing', help: 'error.missingHelp', icon: SearchX },
  closed: { heading: 'error.notTaking', help: 'error.missingHelp', icon: CalendarX2 },
  offline: { heading: 'error.offline', icon: WifiOff },
  error: { heading: 'error.unknown', icon: AlertTriangle },
};

function Problem({ kind }: { kind: ProblemKind }) {
  const { t } = useI18n();
  const copy = PROBLEM_TEXT[kind];
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <IconTile icon={copy.icon} tone={kind === 'error' || kind === 'offline' ? 'warning' : 'muted'} size="lg" />
      <h1 tabIndex={-1} ref={(node) => node?.focus()} className={HEADING}>
        {t(copy.heading)}
      </h1>
      {copy.help && (
        <p className="max-w-md text-base text-muted-foreground">{t(copy.help)}</p>
      )}
      {kind !== 'missing' && kind !== 'closed' && (
        <Button onClick={() => window.location.reload()}>{t('error.retry')}</Button>
      )}
    </div>
  );
}

/** Re-exported so the accessibility test can mount the frame in isolation. */
export { Shell as BookingShell };
export { Button };
